import type { PraiseBadge, StorylineAnnouncementNotificationTarget } from '@ms/yammer-graphql';
import { GroupPrivacy, StorylineAnnouncementAudience } from '@ms/yammer-graphql';
import type { MessageType } from '@ms/yammer-telemetry';
import createCachedSelector from 're-reselect';

import {
  isEventReplyPublisher,
  isEventThreadStarterPublisher,
  isGroupPublisher,
  isGroupQuestionPublisher,
  isGroupReplyPublisher,
  isGroupThreadStarterPublisher,
  isNetworkQuestionNewQuestionPublisher,
  isPollPublisher,
  isPraisePublisher,
  isEditPublisher as isPublisherForEditing,
  isEventPublisher as isPublisherForEvents,
  isTeamsMeetingPublisher as isPublisherForTeamsMeetings,
  isReplyPublisher,
  isTeamsMeetingReplyPublisher,
  isTeamsMeetingThreadStarterPublisher,
  isUserPublisher,
  isUserThreadStarterPublisher,
} from '../../state/publishers/types';
import { isThreadScopeTeamsMeeting } from '../../state/threads/threadScope.types';
import type {
  BaseReplyPublisher,
  Campaign,
  EditMessagePublisher,
  LiveEvent as Event,
  EventPublisher,
  GroupAgent,
  GroupNewQuestionPublisher,
  Message,
  OfficeGroup,
  PrivateGroupMembership,
  PrivateTeamsMeetingInvitee,
  Publisher,
  PublisherType,
  TargetingRule,
  TeamsMeeting,
  TeamsMeetingThreadStarterPublisher,
  Thread,
  Topic,
  User,
  YammerState,
} from '../../state/types';
import { findCampaignReferenceById } from '../campaignReferences/selectors';
import { canViewerPostStorylineAnnouncementForLeader } from '../delegation/selectors';
import { findById as findGroupAgent } from '../groupAgent/selectors';
import { getIsM365CopilotAdoptionGroup } from '../groupFeaturePermissions/selectors';
import { getOnBehalfOfSenders } from '../groupPublisherSettings/selectors';
import { hasGuestsInGroup, isExternal as isGroupExternal, isMultiTenantOrganizationGroup } from '../groups/selectors';
import { getStatusForUserAudienceCounts } from '../leadersManagement/userAudienceCountsSelectors';
import { getIsThreadStarterRestrictedThrows, isCurrentUserAttendeeByEventIdThrows } from '../liveEvents/selectors';
import {
  findByIdThrows as findMessageThrows,
  hasMultiTenantAudience as messageHasMultiTenantAudience,
} from '../messages/selectors';
import { getCurrentMtoHubNetwork } from '../multiTenantOrganization/selectors';
import { isNetworkQuestionsEnabledAndLicensed } from '../networkQuestions/selectors';
import { findById as findOfficeGroup } from '../officeGroups/selectors';
import { getPrivateGroupMembershipId } from '../privateGroupMemberships/getId';
import {
  getPrivateGroupMembershipOfUser,
  isPendingPrivateGroupMembershipRequest,
} from '../privateGroupMemberships/selectors';
import { getPrivateTeamsMeetingInviteeId } from '../privateTeamsMeetingInvitees/getId';
import { canCurrentUserSendOnBehalfOf, getCurrentSessionUser } from '../session/selectors';
import { getStorylinePublisherSettings } from '../storylinePublisherSettings/selectors';
import { findById as findTargetingRule } from '../targetingRules/selectors';
import {
  findTeamsMeetingById,
  findTeamsMeetingByIdThrows,
  isPostAsModeratorFeatureEnabled,
} from '../teamsMeetings/selectors';
import {
  findByIdThrows as findThread,
  isSharePointThread,
  isStorylineOrMediaPostAnnouncementThreadThrows,
  isThreadStarterForId,
  isUserStorylineThread,
} from '../threads/selectors';
import { findByIdThrows as findTopicThrows, getTopicName } from '../topics/selectors';
import {
  findById as findUser,
  getCurrentUser,
  getUsersState,
  isExternal,
  isMtoHubUser,
  isMtoSpokeUser,
} from '../users/selectors';
import { isPrivate, isAdmin as isViewerAdmin } from '../viewerCommunityRelationships/selectors';

import { getNotifiedUsers } from './audienceSelectors';
import { getExistingThreadParticipants } from './existingThreadParticipantSelectors';
import { findById, findByIdThrows } from './findPublisherSelectors';
import { getGroupId } from './getGroupId';
import {
  articlePublisherId,
  dashboardCreatePublisherId,
  homePublisherId,
  leaderPublisherId,
  shareToPublisherId,
  universalCreatePublisherId,
} from './getId';
import { isEditMediaPostPublisher } from './mediaPostPublisherSelectors';
import {
  getUserId,
  isDirect,
  isEditingDirectMessage,
  isEditingReplyToUserStorylineThread,
  isEditingUserStorylineThreadStarter,
  isReplyToUserStorylineThread,
  isStorylinePublisher,
  isTeamsMeetingPublisher,
} from './publisherDestinationSelectors';
import { isGroupOrUserQuestionPublisher } from './publisherTypeSelectors';

export { getGroupId, isNetworkQuestionNewQuestionPublisher };

const emptyPrivateGroupMembershipIds: PrivateGroupMembership['id'][] = [];
const emptyPollOptions: string[] = [];

export const getValidPollOptions = (pollOptions: string[]): string[] =>
  pollOptions.filter((option) => option.trim() !== '');

type CanEventAttendeeStartThread = (state: YammerState, publisher: EventPublisher) => boolean;
export const canEventAttendeeStartThread: CanEventAttendeeStartThread = (state, publisher) => {
  const eventId = publisher.eventId;
  const isThreadStarterRestricted = getIsThreadStarterRestrictedThrows(state, eventId);
  const isAttendee = isCurrentUserAttendeeByEventIdThrows(state, eventId);

  return !(isAttendee && isThreadStarterRestricted);
};

type HasNotifiedUsers = (state: YammerState, id: Publisher['id']) => boolean;
export const hasNotifiedUsers: HasNotifiedUsers = (state, id) => getPublisherNotifiedUserIds(state, id).length > 0;

type GetCurrentlyReplyingMessageToId = (state: YammerState, id: Publisher['id']) => Message['id'] | undefined;
export const getCurrentlyReplyingToMessageId: GetCurrentlyReplyingMessageToId = (state, id) => {
  const publisher = findByIdThrows(state, id);

  if (!isReplyPublisher(publisher)) {
    return;
  }

  return publisher.currentlyReplyingToId;
};

type GetCurrentlyReplyingToThreadId = (state: YammerState, id: Publisher['id']) => Thread['id'] | undefined;
export const getCurrentlyReplyingToThreadId: GetCurrentlyReplyingToThreadId = (state, id) => {
  const publisher = findById(state, id);

  if (!publisher || !isReplyPublisher(publisher)) {
    return;
  }

  return publisher.threadId;
};

type ShouldPublisherHaveReachNotice = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const shouldPublisherHaveReachNotice: ShouldPublisherHaveReachNotice = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return isGroupThreadStarterPublisher(publisher) || isUserThreadStarterPublisher(publisher);
};

type GetEditedMessageId = (state: YammerState, publisherId: Publisher['id']) => string | undefined;
export const getEditedMessageId: GetEditedMessageId = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!isPublisherForEditing(publisher)) {
    return;
  }

  return publisher.messageId;
};

type GetTopicId = (state: YammerState, publisherId: Publisher['id']) => Topic['id'] | undefined;
export const getTopicId: GetTopicId = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (isGroupThreadStarterPublisher(publisher)) {
    return publisher.topicId;
  }

  return;
};

type GetEventId = (state: YammerState, publisherId: Publisher['id']) => Event['id'] | undefined;
export const getEventId: GetEventId = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (isEventThreadStarterPublisher(publisher)) {
    return publisher.eventId;
  }

  return;
};

type GetTeamsMeetingId = (state: YammerState, publisherId: Publisher['id']) => TeamsMeeting['id'] | undefined;
export const getTeamsMeetingId: GetTeamsMeetingId = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (isTeamsMeetingThreadStarterPublisher(publisher)) {
    return publisher.teamsMeetingId;
  }

  return;
};

type GetTeamsMeetingIdForReplyPublisher = (
  state: YammerState,
  publisherId: Publisher['id']
) => TeamsMeeting['id'] | undefined;
export const getTeamsMeetingIdForReplyPublisher: GetTeamsMeetingIdForReplyPublisher = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (isTeamsMeetingReplyPublisher(publisher)) {
    const threadId = publisher.threadId;

    const { scope } = findThread(state, threadId);

    return isThreadScopeTeamsMeeting(scope) ? scope.teamsMeetingId : undefined;
  }

  return;
};

type GetTeamsMeeting = (state: YammerState, publisherId: Publisher['id']) => TeamsMeeting | undefined;
/**
 * @deprecated PERFORMANCE This selector selects an entity object which will force unnecessary re-renders whenever
 * that entity changes and a new object instance is returned. This should be deleted and consumers should call
 * `getPublisherField(publisherId, 'teamsMeetingId')` or `getPublisherTeamsMeetingId(publisherId)` and pass that
 * id to a TeamsMeeting "field" selector for each required field.
 */
export const getTeamsMeeting: GetTeamsMeeting = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isPublisherForTeamsMeetings(publisher) && !isPublisherForEditing(publisher)) {
    return;
  }

  const teamsMeetingId = getPublisherTeamsMeetingId(state, publisherId);

  if (!teamsMeetingId) {
    return;
  }

  return findTeamsMeetingById(state, teamsMeetingId);
};

type GetPublisherTeamsMeetingId = (state: YammerState, publisherId: Publisher['id']) => TeamsMeeting['id'] | undefined;
export const getPublisherTeamsMeetingId: GetPublisherTeamsMeetingId = (state, publisherId) => {
  let threadId: string | undefined;
  const publisher = findByIdThrows(state, publisherId);

  if (isTeamsMeetingThreadStarterPublisher(publisher)) {
    return publisher.teamsMeetingId;
  }

  if (isTeamsMeetingReplyPublisher(publisher)) {
    threadId = publisher.threadId;
  }

  if (isPublisherForEditing(publisher)) {
    threadId = findMessageThrows(state, publisher.messageId).threadId;
  }

  if (!threadId) {
    return;
  }

  const { scope } = findThread(state, threadId);

  return isThreadScopeTeamsMeeting(scope) ? scope.teamsMeetingId : undefined;
};

type IsPublisherInPrivateGroup = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isPublisherInPrivateGroup: IsPublisherInPrivateGroup = (state, publisherId) => {
  const groupId = getGroupId(state, publisherId);

  if (!groupId) {
    return false;
  }

  return isPrivate(state, groupId);
};

type GetPublisherNotifiedUserIds = (state: YammerState, publisherId: Publisher['id']) => Publisher['notifiedUserIds'];
export const getPublisherNotifiedUserIds: GetPublisherNotifiedUserIds = createCachedSelector(
  findByIdThrows,
  (publisher) => publisher.notifiedUserIds
)((_state, id) => id);

type GetNotifedUsersPrivateGroupMembershipIds = (
  state: YammerState,
  publisherId: Publisher['id']
) => PrivateGroupMembership['id'][];
export const getNotifiedUsersPrivateGroupMembershipIds: GetNotifedUsersPrivateGroupMembershipIds = createCachedSelector(
  getGroupId,
  getPublisherNotifiedUserIds,
  hasNotifiedUsers,
  (state) => state.viewerCommunityRelationships,
  (groupId, notifiedUserIds, messageHasNotifiedUsers, viewerCommunityRelationships) => {
    if (!messageHasNotifiedUsers) {
      return emptyPrivateGroupMembershipIds;
    }

    if (!groupId) {
      return emptyPrivateGroupMembershipIds;
    }

    const fulfilledViewerCommunityRelationships = viewerCommunityRelationships.fulfilled;
    const groupViewerCommunityRelationship =
      fulfilledViewerCommunityRelationships && fulfilledViewerCommunityRelationships.byId[groupId];
    const isPrivateGroup =
      groupViewerCommunityRelationship && groupViewerCommunityRelationship.privacy === GroupPrivacy.PRIVATE;
    if (!isPrivateGroup) {
      return emptyPrivateGroupMembershipIds;
    }

    return notifiedUserIds.map((userId) => getPrivateGroupMembershipId(groupId, userId));
  }
)((_state, id) => id);

type HasNonMemberBeenNotified = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasNonMemberBeenNotified: HasNonMemberBeenNotified = (state, publisherId) =>
  getNotifiedUsersPrivateGroupMembershipIds(state, publisherId)
    .filter((membershipId) => {
      const { pending, fulfilled, rejected } = state.privateGroupMemberships;
      const isPending = Object.prototype.hasOwnProperty.call(pending.byId, membershipId);
      const isFulfilled = Object.prototype.hasOwnProperty.call(fulfilled.byId, membershipId);
      const isRejected = Object.prototype.hasOwnProperty.call(rejected.byId, membershipId);
      const isInitialRequestForMembershipIdPending = isPending && !isFulfilled && !isRejected;

      return !isInitialRequestForMembershipIdPending;
    })
    .map((membershipId) => getPrivateGroupMembershipOfUser(state, membershipId))
    .some((isMember) => !isMember);

type GetPublisherNotifiedOfficeUserIds = (
  state: YammerState,
  publisherId: Publisher['id']
) => User['officeUserId'][] | undefined;
export const getPublisherNotifiedOfficeUserIds: GetPublisherNotifiedOfficeUserIds = createCachedSelector(
  getPublisherNotifiedUserIds,
  getUsersState,
  (notifiedUserIds, users) => notifiedUserIds.map((userId) => findUser({ users }, userId)?.officeUserId).filter(Boolean)
)((_state, id) => id);

const emptyPrivateTeamsMeetingInviteeIds: PrivateTeamsMeetingInvitee['id'][] = [];
type GetNotifedUsersPrivateTeamsMeetingInviteeIds = (
  state: YammerState,
  publisherId: Publisher['id']
) => PrivateTeamsMeetingInvitee['id'][];
export const getNotifiedUsersPrivateTeamsMeetingInviteeIds: GetNotifedUsersPrivateTeamsMeetingInviteeIds =
  createCachedSelector(
    getPublisherTeamsMeetingId,
    getPublisherNotifiedOfficeUserIds,
    hasNotifiedUsers,
    getTeamsMeeting,
    (meetingId, notifiedOfficeUserIds, messageHasNotifiedUsers, teamsMeeting) => {
      if (!messageHasNotifiedUsers || !notifiedOfficeUserIds || !meetingId) {
        return emptyPrivateTeamsMeetingInviteeIds;
      }

      return !teamsMeeting || teamsMeeting.isMeetingPrivateToInvitees
        ? notifiedOfficeUserIds.map((officeUserId) => getPrivateTeamsMeetingInviteeId(meetingId, officeUserId))
        : emptyPrivateTeamsMeetingInviteeIds;
    }
  )((_state, id) => id);

type HasNonInviteeBeenNotified = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasNonInviteeBeenNotified: HasNonInviteeBeenNotified = (state, publisherId) => {
  const inviteeIds = getNotifiedUsersPrivateTeamsMeetingInviteeIds(state, publisherId);
  const { fulfilled, rejected } = state.privateTeamsMeetingInvitees;

  return inviteeIds.some((inviteeId) => {
    const isRejected = Object.prototype.hasOwnProperty.call(rejected.byId, inviteeId);
    if (isRejected) {
      return true;
    }

    const fulfilledEntry = fulfilled.byId[inviteeId];

    return fulfilledEntry && !fulfilledEntry.isInvitee;
  });
};

type IsPublisherInMultiTenantOrganizationEnabledTeamsMeeting = (
  state: YammerState,
  publisherId: Publisher['id']
) => boolean;
export const isPublisherInMultiTenantOrganizationEnabledTeamsMeeting: IsPublisherInMultiTenantOrganizationEnabledTeamsMeeting =
  createCachedSelector(getPublisherTeamsMeetingId, getTeamsMeeting, (meetingId, teamsMeeting) => {
    if (!meetingId) {
      return false;
    }

    return !!teamsMeeting && !!teamsMeeting.isMultiTenantOrganizationEnabled;
  })((_state, id) => id);

type HasThreadGuests = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasThreadGuests: HasThreadGuests = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!isGroupReplyPublisher(publisher) && !isEventReplyPublisher(publisher)) {
    return false;
  }

  const { groupId } = publisher;
  if (!isPrivate(state, groupId)) {
    return false;
  }

  const existingThreadParticipants = getExistingThreadParticipants(state, publisherId);

  return existingThreadParticipants.some(
    (userId) => !getPrivateGroupMembershipOfUser(state, getPrivateGroupMembershipId(groupId, userId))
  );
};

type HasExternalNetworkMemberBeenNotified = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasExternalNetworkMemberBeenNotified: HasExternalNetworkMemberBeenNotified = (state, publisherId) =>
  getNotifiedUsers(state, publisherId).some((user) => isExternal(state, user.id));

type HasPendingPrivateGroupMembershipRequest = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasPendingPrivateGroupMembershipRequest: HasPendingPrivateGroupMembershipRequest = (state, publisherId) =>
  getNotifiedUsersPrivateGroupMembershipIds(state, publisherId).some((membershipId) =>
    isPendingPrivateGroupMembershipRequest(state, membershipId)
  );

type HasCampaignReferences = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasCampaignReferences: HasCampaignReferences = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isGroupThreadStarterPublisher(publisher)) {
    return false;
  }

  return publisher.campaignReferences.length > 0;
};

type HasMTOCampaignReference = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasMTOCampaignReference: HasMTOCampaignReference = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return publisher.campaignReferences.some(
    (id: Campaign['id']) => findCampaignReferenceById(state, id)?.scope.type === 'MULTI_TENANT_ORGANIZATION'
  );
};

type HasPrivateGroupSelected = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasPrivateGroupSelected: HasPrivateGroupSelected = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isGroupThreadStarterPublisher(publisher)) {
    return false;
  }

  return !!publisher.groupId && isPrivate(state, publisher.groupId);
};

type GetIsMandatoryNotification = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const getIsMandatoryNotification: GetIsMandatoryNotification = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!publisher.isAnnouncement) {
    return false;
  }

  return publisher.isMandatoryNotification;
};

type IsGroupRecipientRequired = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isGroupRecipientRequired: IsGroupRecipientRequired = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!isGroupThreadStarterPublisher(publisher)) {
    return false;
  }

  return publisher.groupId == null;
};

type IsPublisherExpanded = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isPublisherExpanded: IsPublisherExpanded = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (publisher == null) {
    return false;
  }

  return publisher.isExpanded;
};

type IsM365CopilotAdoptionGroupQuestionPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isM365CopilotAdoptionGroupQuestionPublisher: IsM365CopilotAdoptionGroupQuestionPublisher = (
  state,
  publisherId
) => {
  const publisher = findById(state, publisherId);

  if (publisher == null) {
    return false;
  }

  return (
    isGroupQuestionPublisher(publisher) &&
    publisher.groupId != null &&
    getIsM365CopilotAdoptionGroup(state, publisher.groupId)
  );
};

type IsRelatedQuestionsModuleExpanded = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isRelatedQuestionsModuleExpanded: IsRelatedQuestionsModuleExpanded = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return false;
  }

  if (
    isAnswersCommunityQuestionPublisher(state, publisherId) ||
    isNetworkQuestionNewQuestionPublisher(publisher) ||
    isM365CopilotAdoptionGroupQuestionPublisher(state, publisherId)
  ) {
    return !(publisher as GroupNewQuestionPublisher).hasCollapsedRelatedQuestionsModule;
  }

  return false;
};

type GetHasAttemptedToAttachFile = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const getHasAttemptedToAttachFile: GetHasAttemptedToAttachFile = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (publisher == null) {
    return false;
  }

  return publisher.hasAttemptedToAttachFile;
};

type GetIsBlockedByModeration = (state: YammerState, publisherId: Publisher['id']) => boolean | undefined;
export const getIsBlockedByModeration: GetIsBlockedByModeration = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (publisher == null) {
    return false;
  }

  return !!publisher.isBlockedByModeration;
};

type WasPublishedAsDraft = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const wasPublishedAsDraft: WasPublishedAsDraft = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isGroupThreadStarterPublisher(publisher) && !isUserThreadStarterPublisher(publisher)) {
    return false;
  }

  return publisher.wasPublishedAsDraft;
};

type IsPublisherInExternalGroup = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isPublisherInExternalGroup: IsPublisherInExternalGroup = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return isGroupPublisher(publisher) && publisher.groupId != null && isGroupExternal(state, publisher.groupId);
};

type IsPublisherInGroupWithGuestMembers = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isPublisherInGroupWithGuestMembers: IsPublisherInGroupWithGuestMembers = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return (
    (isGroupPublisher(publisher) || isPublisherForEvents(publisher)) &&
    publisher.groupId != null &&
    hasGuestsInGroup(state, publisher.groupId)
  );
};

type IsEditingDraftMessage = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditingDraftMessage: IsEditingDraftMessage = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!isPublisherForEditing(publisher)) {
    return false;
  }

  const { isDraft } = findMessageThrows(state, publisher.messageId);

  return isDraft;
};

type IsEditingScheduledMessage = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditingScheduledMessage: IsEditingScheduledMessage = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!isPublisherForEditing(publisher)) {
    return false;
  }

  const { isDraft, scheduledPublishAt } = findMessageThrows(state, publisher.messageId);

  return isDraft && !!scheduledPublishAt;
};

type IsEditingDraftGroupMessage = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditingDraftGroupMessage: IsEditingDraftGroupMessage = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isPublisherForEditing(publisher)) {
    return false;
  }

  const groupId = getGroupId(state, publisherId);
  const { isDraft } = findMessageThrows(state, publisher.messageId);

  return isDraft && !!groupId;
};

type IsEditingDraftUserMessage = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditingDraftUserMessage: IsEditingDraftUserMessage = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isPublisherForEditing(publisher)) {
    return false;
  }

  const userId = getUserId(state, publisherId);
  const { isDraft } = findMessageThrows(state, publisher.messageId);

  return isDraft && !!userId;
};

type ShouldShowDeliveryChoices = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const shouldShowDeliveryChoices: ShouldShowDeliveryChoices = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  const canEditPostGroupAnnouncement = isEditingDraftGroupMessage(state, publisherId);
  const canEditPostUserAnnouncement = isEditingDraftUserMessage(state, publisherId);

  if (isGroupThreadStarterPublisher(publisher) || canEditPostGroupAnnouncement) {
    const groupId = getGroupId(state, publisherId);

    if (!groupId) {
      return false;
    }

    if (isViewerAdmin(state, groupId)) {
      return true;
    }

    const onBehalfOfSenders = getOnBehalfOfSenders(state, groupId);
    const onBehalfOfSenderId = getOnBehalfOfSenderId(state, publisherId);

    const delegate = onBehalfOfSenders.find((sender) => sender.nodeId === onBehalfOfSenderId);

    return !!delegate?.canPostAnnouncement;
  }

  if (isUserThreadStarterPublisher(publisher) || canEditPostUserAnnouncement) {
    const userId = getUserId(state, publisherId);

    if (!userId) {
      return false;
    }

    const userAudienceCountsStatus = getStatusForUserAudienceCounts(state, userId);
    if (userAudienceCountsStatus !== 'fulfilled') {
      return false;
    }

    const { id: currentUserId } = getCurrentUser(state);
    if (userId !== currentUserId) {
      const viewerCanPostStorylineAnnouncementOnBehalfOfLeader = canViewerPostStorylineAnnouncementForLeader(
        state,
        userId
      );

      return viewerCanPostStorylineAnnouncementOnBehalfOfLeader;
    }

    const storylinePublisherSettings = getStorylinePublisherSettings(state);

    return !!storylinePublisherSettings?.viewerCanPostStorylineAnnouncement;
  }

  return false;
};

export type PublisherTopicSupport = 'NotSupported' | 'OptionalCollapsible' | 'OptionalAlwaysPresent' | 'Required';

type GetPublisherTopicSupport = (
  state: YammerState,
  publisherId: Publisher['id'],
  isPublisherRedesign?: boolean
) => PublisherTopicSupport;
export const getPublisherTopicSupport: GetPublisherTopicSupport = (state, publisherId, isPublisherRedesign) => {
  const publisher = findByIdThrows(state, publisherId);
  const { isGuest: isCurrentUserGuest } = getCurrentSessionUser(state);

  if (isCurrentUserGuest) {
    return 'NotSupported';
  }

  if (isPublisherRedesign && !isReplyPublisher(publisher)) {
    return 'OptionalCollapsible';
  }

  if (isNetworkQuestionNewQuestionPublisher(publisher)) {
    return 'Required';
  }

  if (
    isGroupThreadStarterPublisher(publisher) ||
    isUserThreadStarterPublisher(publisher) ||
    isEventThreadStarterPublisher(publisher)
  ) {
    return 'OptionalCollapsible';
  }

  if (isEditMediaPostPublisher(state, publisherId)) {
    return 'OptionalAlwaysPresent';
  }

  return 'NotSupported';
};

type GetReplyingToUser = (state: YammerState, publisherId: Publisher['id']) => User | undefined;
/**
 * @deprecated PERFORMANCE This selector selects an entity object which will force unnecessary re-renders whenever that entity
 * changes and a new object instance is returned. This should be deleted and replaced by a `getReplyingToUserId` selector.
 */
export const getReplyingToUser: GetReplyingToUser = (state, publisherId) => {
  const userReplyingToId = getCurrentlyReplyingToMessageId(state, publisherId);
  const userReplyingMessage = userReplyingToId && findMessageThrows(state, userReplyingToId);
  const isThreadStarterMessage = userReplyingMessage && isThreadStarterForId(state, userReplyingMessage.id);
  if (!userReplyingMessage || isThreadStarterMessage) {
    return undefined;
  }

  return findUser(state, userReplyingMessage.sender.id);
};

type GetReplyingToAgent = (state: YammerState, publisherId: Publisher['id']) => GroupAgent | undefined;
/**
 * @deprecated PERFORMANCE This selector selects an entity object which will force unnecessary re-renders whenever that entity
 * changes and a new object instance is returned. This should be deleted and replaced by a `getReplyingToAgentId` selector.
 */
export const getReplyingToAgent: GetReplyingToAgent = (state, publisherId) => {
  const replyingToMessageId = getCurrentlyReplyingToMessageId(state, publisherId);
  const senderReplyingMessage = replyingToMessageId && findMessageThrows(state, replyingToMessageId);
  const isThreadStarterMessage = senderReplyingMessage && isThreadStarterForId(state, senderReplyingMessage.id);

  if (!senderReplyingMessage || isThreadStarterMessage || senderReplyingMessage.sender.schema !== 'groupAgents') {
    return undefined;
  }

  return findGroupAgent(state, senderReplyingMessage.sender.id);
};

type GetPollOptions = (state: YammerState, publisherId: Publisher['id']) => string[];
export const getPollOptions: GetPollOptions = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!isPollPublisher(publisher) && !isPublisherForEditing(publisher)) {
    return emptyPollOptions;
  }

  if (!publisher.pollOptions) {
    return emptyPollOptions;
  }

  return publisher.pollOptions;
};

type HasDuplicatePollOptions = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasDuplicatePollOptions: HasDuplicatePollOptions = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!isPollPublisher(publisher) && !isPublisherForEditing(publisher)) {
    return false;
  }

  const pollOptions = getPollOptions(state, publisherId);
  const validPollOptions = getValidPollOptions(pollOptions);
  const uniquePollOptions = Array.from(new Set([...validPollOptions]));

  return validPollOptions.length > uniquePollOptions.length;
};

type GetPraiseBadge = (state: YammerState, publisherId: Publisher['id']) => PraiseBadge | undefined;
export const getPraiseBadge: GetPraiseBadge = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!isPraisePublisher(publisher)) {
    return;
  }

  return publisher.badge;
};

type CanTeamsMeetingAttendeeStartThread = (
  state: YammerState,
  publisher: TeamsMeetingThreadStarterPublisher
) => boolean;
export const canTeamsMeetingAttendeeStartThread: CanTeamsMeetingAttendeeStartThread = (state, publisher) => {
  const { teamsMeetingId } = publisher;
  const { viewerCanStartThread } = findTeamsMeetingByIdThrows(state, teamsMeetingId);

  return viewerCanStartThread;
};

type IsEditPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditPublisher: IsEditPublisher = (state, publisherId) => {
  const publisher = findById(state, publisherId);
  if (!publisher) {
    return false;
  }

  return isPublisherForEditing(publisher);
};

type IsEditConvertToQuestionPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean | undefined;
export const isEditConvertToQuestionPublisher: IsEditConvertToQuestionPublisher = (state, publisherId) => {
  const publisher = findById(state, publisherId);
  if (!publisher) {
    return false;
  }

  if (isPublisherForEditing(publisher)) {
    return publisher.isConvertingToQuestion;
  }

  return false;
};

type GetOnBehalfOfSenderId = (state: YammerState, publisherId: Publisher['id']) => User['id'] | undefined;
export const getOnBehalfOfSenderId: GetOnBehalfOfSenderId = (state, publisherId) => {
  const publisher = findById(state, publisherId);
  if (!publisher) {
    return undefined;
  }

  if (
    isUserPublisher(publisher) ||
    isGroupPublisher(publisher) ||
    isPublisherForEvents(publisher) ||
    isPublisherForTeamsMeetings(publisher) ||
    isPublisherForEditing(publisher)
  ) {
    return publisher.onBehalfOfSenderId;
  }

  return undefined;
};

type GetIsModeratorMessage = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const getIsModeratorMessage: GetIsModeratorMessage = (state, publisherId) => {
  const publisher = findById(state, publisherId);
  if (!publisher) {
    return false;
  }

  if (isPublisherForTeamsMeetings(publisher)) {
    return publisher.isModeratorMessage;
  }

  return false;
};

type GetIsAnonymousMessage = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const getIsAnonymousMessage: GetIsAnonymousMessage = (state, publisherId) => {
  const publisher = findById(state, publisherId);
  if (!publisher) {
    return false;
  }

  if (isPublisherForTeamsMeetings(publisher)) {
    return publisher.isAnonymousMessage;
  }

  return false;
};

type IsTopicBarOpen = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isTopicBarOpen: IsTopicBarOpen = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return false;
  }

  return publisher.isTopicBarOpen;
};

type IsTopicBarDismissed = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isTopicBarDismissed: IsTopicBarDismissed = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return false;
  }

  return publisher.isTopicBarDismissed;
};

type HasUnpublishedTopics = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasUnpublishedTopics: HasUnpublishedTopics = (state, publisherId) =>
  findByIdThrows(state, publisherId).topicIds.length > 0;

type GetPublisherTopicIdsThrows = (state: YammerState, publisherId: Publisher['id']) => string[];
export const getPublisherTopicIdsThrows: GetPublisherTopicIdsThrows = (state, publisherId) =>
  findByIdThrows(state, publisherId).topicIds;

type GetUnpublishedExistingTopicNames = (state: YammerState, publisherId: Publisher['id']) => Topic['name'][];
/**
 * @deprecated PERFORMANCE This selector will return a new array instance and force unnecessary re-renders
 * EVERY time the store updates (due to the `(state) => state` selector). This should be deleted, consumers should
 * only pass around the topic ids to child components which can use "field" selectors to access a topic's name.
 */
const getUnpublishedExistingTopicNames: GetUnpublishedExistingTopicNames = createCachedSelector(
  (state) => state,
  getPublisherTopicIdsThrows,
  (state, publisherTopicIds) => publisherTopicIds.map((topicId) => getTopicName(state, topicId))
)((_state, id) => id);

type GetUnpublishedTopics = (state: YammerState, publisherId: Publisher['id']) => Topic['name'][];
/**
 * @deprecated PERFORMANCE This should be deleted, consumers should only pass the topic
 * ids to child components which can use "field" selectors to access a topic's name.
 */
export const getUnpublishedTopics: GetUnpublishedTopics = createCachedSelector(
  findByIdThrows,
  getUnpublishedExistingTopicNames,
  ({ topicNames: publisherTopicNames }, topicNamesById) => [...topicNamesById, ...publisherTopicNames]
)((_state, id) => id);

type GetTokensForUnpublishedTopics = (state: YammerState, publisherId: Publisher['id']) => string[];
/**
 * @deprecated PERFORMANCE This selector is not cached. But it should be deleted anyway, consumers should
 * only pass the topic ids to child components which can use "field" selectors to access a topic's name.
 */
export const getTokensForUnpublishedTopics: GetTokensForUnpublishedTopics = (state, publisherId) => {
  const unpublishedTopicIds = getPublisherTopicIdsThrows(state, publisherId);
  const tokensForTopicIds: string[] = [];

  unpublishedTopicIds.forEach((topicId) => {
    const token = findTopicThrows(state, topicId).officeTopicAuthorizationToken?.trim();

    if (token) {
      tokensForTopicIds.push(token);
    }
  });

  return tokensForTopicIds;
};

type GetUnpublishedTopicIdByName = (
  state: YammerState,
  publisherId: Publisher['id'],
  topicName: Topic['name']
) => Topic['id'];
export const getUnpublishedTopicIdByName: GetUnpublishedTopicIdByName = (state, publisherId, topicName) => {
  const topicIds = getPublisherTopicIdsThrows(state, publisherId);
  const unpublishedTopics = topicIds.map((topicId) => state.topics.fulfilled.byId[topicId]);
  const topicToRemove = unpublishedTopics.filter((topic) => topic.name === topicName.trim())[0];

  return topicToRemove?.id;
};

type GetTelemetryMessageTypeFromPostType = (postType: Publisher['postType'] | undefined) => MessageType | undefined;
export const getTelemetryMessageTypeFromPostType: GetTelemetryMessageTypeFromPostType = (postType) => {
  if (!postType) {
    return undefined;
  }

  const postTypeToMessageTypeMap: Record<Publisher['postType'], MessageType> = {
    EventNewQuestion: 'question',
    GroupNewQuestion: 'question',
    TeamsMeetingNewQuestion: 'question',
    UserNewQuestion: 'question',
    GroupNewPraise: 'praise',
    UserNewPraise: 'praise',
    GroupNewPoll: 'poll',
    UserNewPoll: 'poll',
    GroupNewArticle: 'article',
    UserNewArticle: 'article',
    DirectNew: 'discussion',
    DirectReply: 'discussion',
    Edit: 'discussion',
    EventNewUpdate: 'discussion',
    EventReply: 'discussion',
    GroupNewUpdate: 'discussion',
    GroupReply: 'discussion',
    NetworkQuestionNewQuestion: 'discussion',
    NetworkQuestionReply: 'discussion',
    TeamsMeetingNewUpdate: 'discussion',
    TeamsMeetingReply: 'discussion',
    UserNewMedia: 'discussion',
    UserNewUpdate: 'discussion',
    UserReply: 'discussion',
  };

  return postTypeToMessageTypeMap[postType];
};

const postTypeToTelemetryType: Record<Publisher['postType'], PublisherType> = {
  DirectNew: 'direct_message',
  DirectReply: 'reply',
  Edit: 'edit',
  EventNewUpdate: 'event',
  EventNewQuestion: 'event',
  EventReply: 'reply',
  GroupNewUpdate: 'group',
  GroupNewPoll: 'group',
  GroupNewPraise: 'group',
  GroupNewQuestion: 'group',
  GroupNewArticle: 'group',
  GroupReply: 'reply',
  TeamsMeetingNewUpdate: 'teams_meeting',
  TeamsMeetingNewQuestion: 'teams_meeting',
  TeamsMeetingReply: 'reply',
  UserNewUpdate: 'user',
  UserNewPoll: 'user',
  UserNewPraise: 'user',
  UserNewQuestion: 'user',
  UserNewArticle: 'user',
  UserNewMedia: 'user',
  UserReply: 'reply',
  NetworkQuestionNewQuestion: 'network_question',
  NetworkQuestionReply: 'reply',
};

type TelemetryTypeFromPublisherPostType = (
  publisherId: Publisher['id'],
  postType: Publisher['postType'] | undefined
) => PublisherType;
export const telemetryTypeFromPublisherPostType: TelemetryTypeFromPublisherPostType = (publisherId, postType) => {
  if (publisherId === homePublisherId) {
    return 'home';
  }

  if (publisherId === leaderPublisherId) {
    return 'leader';
  }

  if (publisherId === universalCreatePublisherId) {
    return 'universal_create';
  }

  if (publisherId === dashboardCreatePublisherId) {
    return 'dashboard_create';
  }

  if (postType === undefined) {
    return 'group';
  }

  return postTypeToTelemetryType[postType];
};

interface PublisherDraftFeatureFlags {
  readonly isCommunityDraftPostEnabled?: boolean;
  readonly isStorylineDraftPostEnabled?: boolean;
}
type DoesPublisherSupportDrafts = (
  state: YammerState,
  publisherId: Publisher['id'],
  featureFlags: PublisherDraftFeatureFlags
) => boolean;
export const doesPublisherSupportDrafts: DoesPublisherSupportDrafts = (
  state,
  publisherId,
  { isCommunityDraftPostEnabled, isStorylineDraftPostEnabled }
) => {
  const publisher = findByIdThrows(state, publisherId);

  if (isCommunityDraftPostEnabled && isGroupThreadStarterPublisher(publisher)) {
    return true;
  }

  if (isStorylineDraftPostEnabled && isUserThreadStarterPublisher(publisher)) {
    return true;
  }

  return false;
};

type ShouldDisplayUserStorylineItem = (state: YammerState, publisher: Publisher['id']) => boolean;
export const shouldDisplayUserStorylineItem: ShouldDisplayUserStorylineItem = (_state, publisherId) => {
  const isCampaignPublisher = !!getCampaignId(_state, publisherId);

  return (
    isCampaignPublisher ||
    [
      homePublisherId,
      shareToPublisherId,
      universalCreatePublisherId,
      articlePublisherId,
      dashboardCreatePublisherId,
    ].includes(publisherId)
  );
};

type ShouldPublisherShowWarning = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const shouldPublisherShowWarning: ShouldPublisherShowWarning = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  const isDirectEdit = isEditingDirectMessage(state, publisherId);
  const isDirectMessage = isDirect(state, publisherId);

  if (isReplyPublisher(publisher)) {
    return (
      !isSharePointThread(state, publisher.threadId) &&
      !isUserStorylineThread(state, publisher.threadId) &&
      isDirectMessage
    );
  }

  return isDirectEdit;
};

type GetDefaultTopicName = (state: YammerState, publisherId: Publisher['id']) => Topic['name'] | undefined;
/**
 * @deprecated PERFORMANCE Features should pass around topic ids, and use "field" selectors to access the topic name when needed.
 */
export const getDefaultTopicName: GetDefaultTopicName = (state, publisherId) => {
  const topicId = getTopicId(state, publisherId);

  if (!topicId) {
    return undefined;
  }

  return findTopicThrows(state, topicId).name;
};

type GetUserDelegateForStoryline = (state: YammerState, publisherId: Publisher['id']) => User | undefined;
/**
 * @deprecated PERFORMANCE This selector selects an entity object which will force unnecessary re-renders whenever that entity
 * changes and a new object instance is returned. This should be deleted and replaced by a `getUserDelegateIdForStoryline` selector.
 */
export const getUserDelegateForStoryline: GetUserDelegateForStoryline = (state, publisherId) => {
  const delegateId = getOnBehalfOfSenderId(state, publisherId);
  const delegate = delegateId ? findUser(state, delegateId) : undefined;

  return delegate;
};

type GetStorylineAnnouncementAudience = (
  state: YammerState,
  publisherId: Publisher['id']
) => StorylineAnnouncementAudience | undefined;
export const getStorylineAnnouncementAudience: GetStorylineAnnouncementAudience = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isUserThreadStarterPublisher(publisher) && !isPublisherForEditing(publisher)) {
    return undefined;
  }

  return publisher.announcementAudience;
};

type GetStorylineAnnouncementAudienceIds = (state: YammerState, publisherId: Publisher['id']) => string[] | undefined;
export const getStorylineAnnouncementAudienceIds: GetStorylineAnnouncementAudienceIds = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher || (!isUserThreadStarterPublisher(publisher) && !isPublisherForEditing(publisher))) {
    return undefined;
  }

  return publisher.announcementAudienceIds;
};

type GetStorylineAnnouncementTemporaryAudienceIds = (
  state: YammerState,
  publisherId: Publisher['id']
) => string[] | undefined;
export const getStorylineAnnouncementTemporaryAudienceIds: GetStorylineAnnouncementTemporaryAudienceIds = (
  state,
  publisherId
) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isUserThreadStarterPublisher(publisher) && !isPublisherForEditing(publisher)) {
    return undefined;
  }

  return publisher.announcementTemporaryAudienceIds;
};

type GetPublisherCampaignHashtag = (state: YammerState, publisherId: Publisher['id']) => string | undefined;
export const getPublisherCampaignHashtag: GetPublisherCampaignHashtag = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (isGroupThreadStarterPublisher(publisher) || isUserThreadStarterPublisher(publisher)) {
    return publisher.campaignHashtag;
  }

  return;
};

type GetCampaignId = (state: YammerState, publisherId: Publisher['id']) => Campaign['id'] | undefined;
export const getCampaignId: GetCampaignId = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (publisher && (isGroupThreadStarterPublisher(publisher) || isUserThreadStarterPublisher(publisher))) {
    return publisher.campaignId;
  }

  return;
};

type GetStorylineAnnouncementNotificationTargets = (
  state: YammerState,
  publisherId: Publisher['id']
) => StorylineAnnouncementNotificationTarget[] | undefined;
export const getStorylineAnnouncementNotificationTargets: GetStorylineAnnouncementNotificationTargets = (
  state,
  publisherId
) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isUserThreadStarterPublisher(publisher) && !isPublisherForEditing(publisher)) {
    return undefined;
  }

  return publisher.announcementNotificationTargets;
};

type GetPublisherTitle = (state: YammerState, publisherId: Publisher['id']) => string | undefined;
export const getPublisherTitle: GetPublisherTitle = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  return publisher?.title;
};

type GetPublisherAnnouncementNotificationTargetEntities = (
  state: YammerState,
  publisherId: Publisher['id']
) =>
  | {
      readonly users: User[] | undefined;
      readonly officeGroups: OfficeGroup[] | undefined;
      readonly targetingRules: TargetingRule[] | undefined;
    }
  | undefined;
/**
 * @deprecated PERFORMANCE This selector will return a new object instance and force re-renders on EVERY store
 * update. This selector should be deleted, and individual subcomponents should use "field" selectors to request
 * only the specific fields from a user/group/etc. entities they require.
 */
export const getPublisherAnnouncementNotificationTargetEntities: GetPublisherAnnouncementNotificationTargetEntities =
  createCachedSelector(
    (state: YammerState, publisherId: Publisher['id']) => findById(state, publisherId),
    (state: YammerState) => state,
    (publisher, state) => {
      if (!publisher || !isPublisherForEditing(publisher)) {
        return undefined;
      }

      const users = publisher?.announcementNotificationTargetEntityReferences
        ?.filter((entity) => entity.schema === 'users')
        .map((entity) => findUser(state, entity.id))
        .filter((user) => user !== undefined)!;

      const officeGroups = publisher?.announcementNotificationTargetEntityReferences
        ?.filter((entity) => entity.schema === 'officeGroups')
        .map((entity) => findOfficeGroup(state, entity.id))
        .filter((officeGroup) => officeGroup !== undefined)!;

      const targetingRules = publisher?.announcementNotificationTargetEntityReferences
        ?.filter((entity) => entity.schema === 'targetingRules')
        .map((entity) => findTargetingRule(state, entity.id))
        .filter((targetingRule) => targetingRule !== undefined)!;

      return { users, officeGroups, targetingRules };
    }
  )((_state, publisherId) => publisherId);

type HasMultiTenantAudience = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasMultiTenantAudience: HasMultiTenantAudience = (state, publisherId) =>
  getStorylineAnnouncementAudience(state, publisherId) === StorylineAnnouncementAudience.MULTI_TENANT_ORGANIZATION;

type IsMultiTenantStoryline = (
  state: YammerState,
  publisherId: Publisher['id'],
  isEditing: boolean,
  isReply: boolean
) => boolean;
// eslint-disable-next-line complexity
const isMultiTenantStoryline: IsMultiTenantStoryline = (state, publisherId, isEditing, isReply) => {
  let threadId = '';
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return false;
  }

  const isStorylineThreadStarterPublisher =
    isStorylinePublisher(state, publisherId) &&
    !isReplyToUserStorylineThread(state, publisherId) &&
    !isPublisherForEditing(publisher);
  const isEditingStorylineThreadStarter = isEditingUserStorylineThreadStarter(state, publisherId);
  const isStorylineReply = isReplyToUserStorylineThread(state, publisherId);
  const isEditingStorylineReply = isEditingReplyToUserStorylineThread(state, publisherId);

  if (!isReply && !isEditing && !isStorylineThreadStarterPublisher) {
    return false;
  }

  if (!isReply && isEditing && !isEditingStorylineThreadStarter) {
    return false;
  }

  if (isReply && !isEditing && !isStorylineReply) {
    return false;
  }

  if (isReply && isEditing && !isEditingStorylineReply) {
    return false;
  }

  const { id: currentUserId } = getCurrentUser(state);
  const isHubUser = isMtoHubUser(state, currentUserId);
  const isSpokeUser = isMtoSpokeUser(state, currentUserId);
  const isMtoUser = isReply ? isHubUser || isSpokeUser : isHubUser;
  const hubNetwork = getCurrentMtoHubNetwork(state);

  if (isEditing) {
    threadId = findMessageThrows(state, (publisher as EditMessagePublisher).messageId).threadId;
  } else if (isReply) {
    threadId = (publisher as BaseReplyPublisher).threadId;
  }

  const thread = threadId ? findThread(state, threadId) : undefined;
  const isThreadFromHub = thread && thread?.networkId === hubNetwork?.id;

  if (isMtoUser) {
    if (isStorylineThreadStarterPublisher) {
      if (publisher.isAnnouncement) {
        return hasMultiTenantAudience(state, publisherId);
      }

      return true;
    }

    if (isThreadFromHub) {
      if (isStorylineOrMediaPostAnnouncementThreadThrows(state, threadId)) {
        const threadMessageId = thread.threadStarterId;

        return messageHasMultiTenantAudience(state, threadMessageId);
      }

      return true;
    }
  }

  return false;
};

export const isCreatingMultiTenantStorylinePost = (state: YammerState, publisherId: Publisher['id']): boolean =>
  isMultiTenantStoryline(state, publisherId, false, false);
export const isCreatingMultiTenantStorylineReply = (state: YammerState, publisherId: Publisher['id']): boolean =>
  isMultiTenantStoryline(state, publisherId, false, true);
export const isEditingMultiTenantStorylinePost = (state: YammerState, publisherId: Publisher['id']): boolean =>
  isMultiTenantStoryline(state, publisherId, true, false);
export const isEditingMultiTenantStorylineReply = (state: YammerState, publisherId: Publisher['id']): boolean =>
  isMultiTenantStoryline(state, publisherId, true, true);

type IsMultiTenantStorylinePost = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isMultiTenantStorylinePost: IsMultiTenantStorylinePost = (state, publisherId) =>
  isCreatingMultiTenantStorylinePost(state, publisherId) || isEditingMultiTenantStorylinePost(state, publisherId);

type IsMultiTenantStorylineReply = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isMultiTenantStorylineReply: IsMultiTenantStorylineReply = (state, publisherId) =>
  isCreatingMultiTenantStorylineReply(state, publisherId) || isEditingMultiTenantStorylineReply(state, publisherId);

type IsMultiTenantGroupPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isMultiTenantGroupPublisher: IsMultiTenantGroupPublisher = (
  state: YammerState,
  publisherId: Publisher['id']
) => {
  const groupId = getGroupId(state, publisherId);

  return !!(groupId && isMultiTenantOrganizationGroup(state, groupId));
};

type IsMultiTenantGroupPost = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isMultiTenantGroupPost: IsMultiTenantGroupPost = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  return !!(
    publisher &&
    isMultiTenantGroupPublisher(state, publisherId) &&
    (isGroupThreadStarterPublisher(publisher) || isEditingDraftGroupMessage(state, publisherId))
  );
};

type IsMultiTenantGroupReply = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isMultiTenantGroupReply: IsMultiTenantGroupReply = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  return !!(publisher && isMultiTenantGroupPublisher(state, publisherId) && isGroupReplyPublisher(publisher));
};

type ShouldShowAskMeAnythingPostOnBehalfOfBar = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const shouldShowAskMeAnythingPostOnBehalfOfBar: ShouldShowAskMeAnythingPostOnBehalfOfBar = (
  state,
  publisherId
) => {
  const canSendOnBehalfOf = canCurrentUserSendOnBehalfOf(state);
  const { canScheduleAndCreateTeamsMeeting } = getCurrentSessionUser(state);

  if (!canSendOnBehalfOf || !canScheduleAndCreateTeamsMeeting) {
    return false;
  }

  return isTeamsMeetingPublisher(state, publisherId);
};

type ShouldShowTeamsQnAPostAsModerator = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const shouldShowTeamsQnAPostAsModerator: ShouldShowTeamsQnAPostAsModerator = (state, publisherId) => {
  if (!isPostAsModeratorFeatureEnabled(state)) {
    return false;
  }

  const publisher = findById(state, publisherId);
  if (!publisher) {
    return false;
  }

  if (isPublisherForEditing(publisher)) {
    return false;
  }

  return getTeamsMeeting(state, publisherId)?.viewerIsAdmin ?? false;
};

type IsAnswersCommunityQuestionPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isAnswersCommunityQuestionPublisher: IsAnswersCommunityQuestionPublisher = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return false;
  }

  return isGroupQuestionPublisher(publisher) && isNetworkQuestionsEnabledAndLicensed(state);
};

type isNetworkQuestionPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isNetworkQuestionPublisher: isNetworkQuestionPublisher = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return false;
  }

  return isNetworkQuestionNewQuestionPublisher(publisher) && isNetworkQuestionsEnabledAndLicensed(state);
};

type IsVivaEngageQuestionPublisherWithTitle = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isVivaEngageQuestionPublisherWithTitle: IsVivaEngageQuestionPublisherWithTitle = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return isGroupOrUserQuestionPublisher(state, publisherId) && !isNetworkQuestionNewQuestionPublisher(publisher);
};

type GetQuestionPromptState = (state: YammerState, publisherId: Publisher['id']) => Publisher['questionPromptState'];
export const getQuestionPromptState: GetQuestionPromptState = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  return publisher?.questionPromptState;
};

type IsPublisherForDraftCuratedThread = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isPublisherForDraftCuratedThread: IsPublisherForDraftCuratedThread = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isPublisherForEditing(publisher)) {
    return false;
  }

  const { isDraft, threadId } = findMessageThrows(state, publisher.messageId);

  if (!isDraft) {
    return false;
  }

  const { messageContentSourceFileId } = findThread(state, threadId);

  if (!messageContentSourceFileId) {
    return false;
  }

  return !!messageContentSourceFileId;
};
